import { Check, Close } from "@mui/icons-material";
import {
  Avatar,
  Box,
  Grid,
  IconButton,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Typography,
} from "@mui/material";
import { format } from "date-fns";
import { useState } from "react";
import {
  hoursMinutesFormat,
  shortDateFormat,
} from "../../../helpers/formatDateTime";
import { ReactComponent as DeleteIcon } from "../../../icons/deleteDark.svg";
import { ReactComponent as EditIcon } from "../../../icons/editGray.svg";
import CommonTextField from "../commonTextField/CommonTextField";
import OverlaySpinner from "../overlaySpinner/OverlaySpinner";
import { CommenterName } from "./styles";
import { CommentDisplay } from "./types";

function decodeHtml(html: string) {
  const txt = document.createElement("textarea");
  txt.innerHTML = html;
  return txt.value;
}

type Props = {
  comments: CommentDisplay[];
  currentUserId: string | null;
  onEdit: (id: string, newText: string) => Promise<boolean>;
  onDelete: (id: string) => Promise<boolean>;
  deleteConfirmationText: string;
};

const CommentHistoryList = ({
  comments,
  currentUserId,
  onEdit,
  onDelete,
  deleteConfirmationText,
}: Props) => {
  const [editingComment, setEditingComment] = useState<
    { id: string; commentText: string } | undefined
  >();
  const [deletingCommentId, setDeletingCommentId] = useState<
    string | undefined
  >();
  const [isLoading, setIsLoading] = useState(false);

  const handleConfirm = async (comment: CommentDisplay) => {
    setIsLoading(true);
    if (deletingCommentId) {
      const success = await onDelete(comment.id).finally(() => {
        setIsLoading(false);
      });
      if (success) {
        setDeletingCommentId(undefined);
      }
    } else if (editingComment) {
      const success = await onEdit(
        comment.id,
        editingComment.commentText,
      ).finally(() => {
        setIsLoading(false);
      });
      if (success) {
        setEditingComment(undefined);
      }
    }
  };

  const handleCancel = () => {
    setDeletingCommentId(undefined);
    setEditingComment(undefined);
  };

  return (
    <List sx={{ width: "inherit" }}>
      {[...comments]
        .sort((a, b) => b.dateTime - a.dateTime)
        .map((comment) => {
          const date = format(comment.dateTime, shortDateFormat);
          const time = format(comment.dateTime, hoursMinutesFormat);
          const isEditing = editingComment?.id === comment.id;
          const isDeleting = deletingCommentId === comment.id;

          return (
            <ListItem
              alignItems="flex-start"
              key={comment.id}
              sx={{
                padding: "4px 8px",
                display: "flex",
                alignItems: "flex-start",
              }}
            >
              <ListItemAvatar sx={{ minWidth: "44px", marginTop: "12px" }}>
                <Avatar sx={{ width: "32px", height: "32px" }} />
              </ListItemAvatar>
              <ListItemText
                primary={
                  <Grid container>
                    <Grid
                      item
                      container
                      justifyContent="space-between"
                      gap={0.5}
                    >
                      <Grid item container gap={0.5} style={{ width: "unset" }}>
                        <Grid item alignSelf="center">
                          <CommenterName variant="secondary">
                            {comment.authorName}
                          </CommenterName>
                        </Grid>
                        <Grid item alignSelf="center">
                          <Typography variant="secondary">{date}</Typography>
                        </Grid>
                        <Grid item alignSelf="center">
                          <Typography variant="secondary">{time}</Typography>
                        </Grid>
                      </Grid>
                      <Grid item container style={{ width: "unset" }}>
                        {!isDeleting &&
                          !isEditing &&
                          currentUserId === comment.authorId && (
                            <Grid item alignSelf="center">
                              <IconButton
                                onClick={() => {
                                  setDeletingCommentId(undefined);
                                  setEditingComment({
                                    id: comment.id,
                                    commentText: comment.comment,
                                  });
                                }}
                              >
                                <EditIcon />
                              </IconButton>
                              {comment.canDelete !== false && (
                                <IconButton
                                  size="small"
                                  onClick={() => {
                                    setEditingComment(undefined);
                                    setDeletingCommentId(comment.id);
                                  }}
                                >
                                  <DeleteIcon
                                    style={{ width: "24px", height: "24px" }}
                                  />
                                </IconButton>
                              )}
                            </Grid>
                          )}
                        {(isDeleting || isEditing) && (
                          <Grid item>
                            <IconButton
                              onClick={() => handleConfirm(comment)}
                              size="small"
                            >
                              <Check style={{ color: "green" }} />
                            </IconButton>
                            <IconButton onClick={handleCancel} size="small">
                              <Close style={{ color: "red" }} />
                            </IconButton>
                          </Grid>
                        )}
                      </Grid>
                    </Grid>
                    <Grid item container>
                      {isDeleting && (
                        <Grid container pb={2} justifyContent="space-between">
                          <Grid item alignSelf="center">
                            <Typography
                              variant="secondary"
                              sx={{ color: "primary.main" }}
                              fontWeight="600"
                            >
                              {deleteConfirmationText}
                            </Typography>
                          </Grid>
                          <OverlaySpinner
                            isLoading={isLoading}
                            sx={{ position: "absolute" }}
                            spinnerSize={24}
                          />
                        </Grid>
                      )}
                      <Grid container sx={{ justifyContent: "flex-end" }}>
                        <Grid
                          item
                          sx={{
                            width: "inherit",
                            marginTop: isEditing ? "2px" : "-4px",
                          }}
                        >
                          {isEditing ? (
                            <Box position="relative">
                              {comment.displayPrefix && (
                                <Typography
                                  variant="inherit"
                                  sx={{
                                    lineHeight: "20px",
                                    fontWeight: 600,
                                    marginBottom: "4px",
                                  }}
                                >
                                  {comment.displayPrefix}:
                                </Typography>
                              )}
                              <CommonTextField
                                sx={{ width: "100%" }}
                                size="small"
                                onChange={(e) =>
                                  setEditingComment({
                                    id: comment.id,
                                    commentText: e.target.value,
                                  })
                                }
                                multiline
                                value={editingComment?.commentText ?? ""}
                              />
                              <OverlaySpinner
                                isLoading={isLoading}
                                sx={{ position: "absolute" }}
                                spinnerSize={24}
                              />
                            </Box>
                          ) : (
                            <Typography
                              variant={isDeleting ? "disabled" : "inherit"}
                              sx={{
                                lineHeight: "20px",
                                wordWrap: "break-word",
                                hyphens: "auto",
                                width: "inherit",
                              }}
                            >
                              {comment.displayPrefix
                                ? `${comment.displayPrefix}: ${decodeHtml(comment.comment)}`
                                : decodeHtml(comment.comment)}
                            </Typography>
                          )}
                        </Grid>
                      </Grid>
                    </Grid>
                  </Grid>
                }
              />
            </ListItem>
          );
        })}
    </List>
  );
};

export default CommentHistoryList;
